// src/data/filedCopy.js
//
// RULING 271 (2026-10-02): THE FILER KEEPS A COPY. On submit, the person who filed is given the complete document as
// filed (all pages), to keep - the same principle as "Retain a copy of both pages." The platform's own copy is
// unchanged and remains the record: this READS NOTHING BACK INTO THE CLAIM AND WRITES NOTHING.
//
// WHAT ALREADY EXISTED (PD-10), AND WHAT THIS ADDS. The claim page has always offered the form as PDF and Word at any
// time (src\components\ClaimFormPanel.js - "Download PDF" / "Download Word"). Nothing was given AT FILING: NewClaim
// showed a claim number, QuickFile went straight to the claims list. This gives the filer the same form at that
// moment, built by the SAME builder the claim page uses (penaltyClaim.js claimFormModel) from the record exactly as it
// was written, drawn by the same PDF writer (penaltyClaimPdf.js - every page, "Page X of Y"). Where the form is not
// yet complete it carries PREVIEW, exactly as the claim page would show it: the copy is what was filed, not more.
// The loads below are the three ClaimFormPanel makes (GC of record, the filer's profile, the property's service
// setting), each best-effort exactly as there - a refused read leaves the form as the claim page would draw it for
// that person.
//
// Withdrawable by number: RULING_271_FILER_COPY false and filerCopy() returns { ok: false, off: true }; the filing
// screens then give nothing, as before.
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { claimFormModel, formRoleOf, claimFormFileName } from './penaltyClaim';
import { penaltyClaimPdf } from './penaltyClaimPdf';
import { getClaimRequirements } from './claimRequirements';
import { crossCheck, entryFor } from './presentedToCheck';   // Record 610 (ruling 262)
import { officeAddressFor } from './contactDetails';   // Record 610 (ruling 259)
import { loadDirectories } from './directoryLoad';   // Record 610: the same loads the claim page makes
import { serviceFor, agreementSlug } from './serviceSettings';
import { resolveRuleText } from './ruleResolver';
import { claimCommittee } from './claimPayload';

export const RULING_271_FILER_COPY = true;
const LOGO = { path: '/smart-logo-2017.jpg', w: 200, h: 33 };   // the claim page's letterhead logo (ClaimFormPanel)
const s = (v) => (v == null ? '' : String(v).trim());

// record: the claim document exactly as written at filing (the payload), with its claim number as `id`.
// Returns { ok, url, fileName, pages, sendable, error } - url is an object URL the caller revokes when done.
export async function filerCopy(record, user) {
  if (!RULING_271_FILER_COPY) return { ok: false, off: true };
  try {
    const claim = { ...(record || {}) };
    const committee = claimCommittee(claim);
    const uid = user && user.uid;
    const [gcName, me, svc, logo, dirs] = await Promise.all([
      committee ? getDocs(query(collection(db, 'officeTerms'), where('officeId', '==', committee)))
        .then(snap => { const open = snap.docs.map(d => d.data()).filter(t => t && !t.endedOn && s(t.holderName)); return open.length === 1 ? s(open[0].holderName) : ''; })
        .catch(() => '') : Promise.resolve(''),
      uid ? getDoc(doc(db, 'users', uid)).then(d => (d.exists() ? d.data() : null)).catch(() => null) : Promise.resolve(null),
      committee ? getDoc(doc(db, 'serviceSettings', committee)).then(d => (d.exists() ? d.data() : {})).catch(() => ({})) : Promise.resolve({}),
      fetch(LOGO.path).then(r => (r.ok ? r.arrayBuffer() : null)).then(b => (b ? { bytes: new Uint8Array(b), w: LOGO.w, h: LOGO.h } : null)).catch(() => null),
      loadDirectories(db, committee, claim.scopeKey),   // Record 610: best-effort, nulls on a refused read
    ]);
    const req = getClaimRequirements(claim.applicableAgreement);
    const by = formRoleOf(user);
    const declined = claim.claimantDeclinedSignature && claim.claimantDeclinedSignature.choice === 'declined';
    const model = claimFormModel(claim, { committee, gcName, me, by, claimantSigns: !declined, user,
      requirements: req, resolveRule: resolveRuleText, service: serviceFor(claim.applicableAgreement, req, svc) || undefined,
      // Record 610 (rulings 259/262): the same office address and the same three-way check the claim page draws.
      officeAddress: officeAddressFor(by, { letterhead: dirs.committeeDoc && dirs.committeeDoc.letterhead, local: dirs.lc }),
      presentedToCheck: crossCheck({ answer: claim.presentedTo, agreement: claim.applicableAgreement, designation: req && req.presentmentOfficer,
        gc: entryFor(dirs.gc, agreementSlug(claim.applicableAgreement)), lc: entryFor(dirs.lc, agreementSlug(claim.applicableAgreement)),
        recorded: claim.presentedToCheck }) });
    const pdf = penaltyClaimPdf(model, { logo: logo || undefined, preview: !model.sendable, sendable: model.sendable });
    const url = URL.createObjectURL(new Blob([pdf.bytes], { type: 'application/pdf' }));
    return { ok: true, url, fileName: claimFormFileName(claim, model, 'FILED_COPY'), pages: pdf.pages, sendable: model.sendable };
  } catch (e) {
    return { ok: false, error: (e && e.message) || 'the copy could not be drawn' };
  }
}

// Hand the file to the filer - the same anchor-click the claim page uses for its Word download.
export function saveCopy(copy) {
  if (!copy || !copy.ok || !copy.url) return false;
  try {
    const a = document.createElement('a'); a.href = copy.url; a.download = copy.fileName;
    document.body.appendChild(a); a.click(); a.remove();
    return true;
  } catch (e) { return false; }
}
